"use client";
import React, {
	useState,
	useEffect,
	useRef,
	forwardRef,
	useImperativeHandle,
	ChangeEvent,
} from "react";
import Image from "next/image";

interface ContentType {
	id: string | number;
	title: string;
	image?: string;
}

interface SelectInputProps {
	value?: string;
	listClassName?: string;
	className?: string;
	fieldName: string;
	isEditAble?: boolean;
	title?: string;
	content: ContentType[];
	onChange: (selectedValue: string, fieldName: string) => void;
	placeHolder?: boolean;
	labelVisible?: boolean;
	fromProfile?: boolean;
}

// Expose this handle type from the parent via ref
export interface DropdownHandle {
	openDropdown: () => void;
}

const SelectInput = forwardRef<DropdownHandle, SelectInputProps>(
	(
		{
			value,
			fieldName,
			className,
			title,
			content,
			listClassName,
			onChange,
			labelVisible = true,
			isEditAble = false,
			placeHolder = true,
			fromProfile = false,
		},
		ref,
	) => {
		const [isOpen, setIsOpen] = useState<boolean>(false);
		const [searchTerm, setSearchTerm] = useState<string>("");
		const [filteredOptions, setFilteredOptions] =
			useState<ContentType[]>(content);
		const dropdownRef = useRef<HTMLDivElement>(null);
		const inputRef = useRef<HTMLInputElement>(null);

		// Expose openDropdown() to parent via ref
		useImperativeHandle(ref, () => ({
			openDropdown: () => {
				if (!isEditAble) {
					setIsOpen(true);
					// Also focus the input so keyboard navigation works immediately
					setTimeout(() => inputRef.current?.focus(), 0);
				}
			},
		}));

		useEffect(() => {
			if (searchTerm.trim() === "") {
				setFilteredOptions(content);
			} else {
				const filtered = content.filter((item) =>
					item.title.toLowerCase().includes(searchTerm.toLowerCase()),
				);
				setFilteredOptions(filtered);
			}
		}, [searchTerm, content]);

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent): void => {
				if (
					dropdownRef.current &&
					!dropdownRef.current.contains(event.target as Node)
				) {
					setIsOpen(false);
					setSearchTerm("");
				}
			};

			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}, []);

		const handleSelect = (option: ContentType): void => {
			onChange(option.title, fieldName);
			setIsOpen(false);
			setSearchTerm("");
		};

		const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
			setSearchTerm(e.target.value);
		};

		const selectedTitle =
			content.find((item) => item.title === value)?.title || "";

		return (
			<div className="relative w-full" ref={dropdownRef}>
				<div className="relative">
					{labelVisible && placeHolder && (
						<span
							className={`absolute left-3 text-black text-[14px] font-semibold ff_poppins transition-all duration-300 ease-in-out !z-20 ${
								value ? "-top-[25px] opacity-100" : "top-3 opacity-0"
							}`}>
							{title}
						</span>
					)}
					<input
						ref={inputRef}
						type="text"
						disabled={isEditAble}
						className={`relative z-20 w-full h-[50px] px-4 py-2 ${
							!fromProfile
								? "bg-[#3777ff] placeholder:text-white placeholder:opacity-60 placeholder:font-medium text-white"
								: "bg-white !placeholder:text-black/60 text-black"
						} rounded-[8px] border border-gray-300 text-sm sm:text-base font-semibold ff_poppins outline-none duration-300 ease-in-out ${className} ${
							isEditAble && "opacity-50 cursor-not-allowed"
						}`}
						placeholder={title}
						value={isOpen ? searchTerm : selectedTitle}
						onChange={handleInputChange}
						onFocus={() => setIsOpen(true)}
					/>
				</div>

				{isOpen && (
					<ul
						className={`absolute scrollBarHidden z-40 w-full mt-1 rounded-[8px] shadow-lg max-h-60 overflow-y-auto ff_poppins scrollbar-thin scrollbar-thumb-[#2367f9] scrollbar-track-[#3777ff]/30 duration-200 ease-in-out ${listClassName} ${
							!fromProfile
								? "bg-[#3777ff] border-0"
								: "bg-white border border-gray-300"
						}`}>
						{filteredOptions.length > 0 ? (
							filteredOptions.map((option) => (
								<li
									key={option.id}
									onClick={() => handleSelect(option)}
									className={`px-4 py-2 flex justify-between items-center cursor-pointer ${
										!fromProfile && "hover:bg-[#2367f9]"
									} transition duration-200 ease-in-out ${listClassName}`}>
									<span
										className={`${
											!fromProfile ? "text-white" : "text-black"
										} text-sm sm:text-base duration-200 ease-in-out font-medium ff_poppins ${listClassName}`}>
										{option.title}
									</span>
									{option.image && (
										<Image
											src={option.image}
											alt={option.title}
											width={36}
											height={24}
											className="ml-2"
										/>
									)}
								</li>
							))
						) : (
							<li
								className={`px-4 py-2 ${
									!fromProfile ? "text-white" : "text-black"
								} text-sm duration-200 ease-in-out ${listClassName}`}>
								No results found
							</li>
						)}
					</ul>
				)}
			</div>
		);
	},
);

SelectInput.displayName = "SelectInput";

export default SelectInput;
